import ChangeLogs from '@gp/components/ChangeLogs';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import LinkJump from '@gp/components/LinkJump';
import PlatformIcon from '@gp/components/PlatformIcon';
import { GRStatusTag } from '@gp/components/StatusTag';
import {
  MENU_ID_MAP,
  PLATFORM_NAMES,
  SLASH_G_POP,
  StaticGatereviewDecisionsCodes,
} from '@gp/constants/const';
import { queryJumpable } from '@gp/utils/common';
import { formatMilestoneDate, getMilestoneCorrectName } from '@gp/utils/milestone';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';
import type { IBaseProps } from '@home/types/common';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { history } from 'umi';
import './index.scss';

interface ICardProps extends IBaseProps {
  isStudioCalendar?: boolean; // studio-calendar页面的卡片
  isDashboardCalendar?: boolean; // Pipeline/Dashboard页面 Schedule模块日历卡片
  type: number; // -1: 未知 0: 浅蓝色 1: 深蓝色
  isTBC: boolean; // 切角效果
  isOverdue: boolean; // 过期透明度效果
  cardList: CommonCalendarCardItemTypes | null;
  fullScreenContent: React.RefObject<HTMLElement | null> | null;
  onCardClick?:
    | ((cardList: CommonCalendarCardItemTypes | null, isJump: boolean) => string | undefined)
    | null;
}

const BG_MAP: { [keyName: number]: string } = {
  // 0: '#CFE4FF',
  // 1: '#DEF2E4',
  // 2: '#EBECF1',
  0: '#cfe4ff',
  1: '#ffcccc',
  2: '#B8BED1',
  3: '#DEF2E4',
};

const PIPELINE_OVERVIEW_SCHEDULE_GATEREVIEW_DECISIONS_CODES = [
  StaticGatereviewDecisionsCodes.Pass,
  StaticGatereviewDecisionsCodes.Fail,
  StaticGatereviewDecisionsCodes.Redo,
  StaticGatereviewDecisionsCodes.AwaitingApproval,
  StaticGatereviewDecisionsCodes.Empty,
];

const getSortedMilestoneHistories = (list?: any[]): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return [...list].sort((a, b) => {
    const aUpdateTime = a?.update_time;
    const bUpdateTime = b?.update_time;
    const aUpdateTimeM: moment.Moment | null = aUpdateTime ? moment(aUpdateTime) : null;
    const bUpdateTimeM: moment.Moment | null = bUpdateTime ? moment(bUpdateTime) : null;
    const isAUpdateTimeMValid = aUpdateTimeM?.isValid();
    const isBUpdateTimeMValid = bUpdateTimeM?.isValid();
    if (!isAUpdateTimeMValid && !isBUpdateTimeMValid) return 0;
    if (!isAUpdateTimeMValid) return 1;
    if (!isBUpdateTimeMValid) return -1;
    if (aUpdateTimeM?.isSame(bUpdateTimeM)) return 0;
    return aUpdateTimeM?.isBefore(bUpdateTimeM) ? 1 : -1;
  });
};

const getMilestoneHistories = (list?: any[]): any[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const {
      date_confirm: dateConfirm,
      date,
      reason,
      modify_personnel: updateUser,
      update_time: updateTime,
    } = item || {};
    return {
      dateConfirm,
      date,
      reason,
      updateUser,
      updateTime,
    };
  });
};

const Card: React.FC<ICardProps> = (props) => {
  const {
    style,
    className,
    isStudioCalendar = false,
    isDashboardCalendar = false,
    type = -1,
    isTBC = false,
    isOverdue = true,
    cardList,
    fullScreenContent,
    onCardClick,
  } = props;

  const [isHoverCard, setIsHoverCard] = useState(false);

  const isNoData = useMemo<boolean>(() => !cardList || type === -1, [cardList, type]);

  const date = useMemo<string>(() => {
    const { date } = cardList || {};
    return formatMilestoneDate(date, isTBC);
  }, [isTBC, cardList]);

  // 获取卡片是否可以跳转页面
  const isPageSkipable = useMemo<boolean>(() => {
    let res = !isNoData && type !== -1;
    if (isStudioCalendar && type === 0) {
      res = res && !!cardList?.projectId2 && !!cardList?.meetingId && !!cardList?.isJump2;
    } else {
      res = res && !!cardList?.projectId && !!cardList?.isJump;
    }

    return res;
  }, [
    isNoData,
    cardList?.projectId,
    cardList?.projectId2,
    cardList?.isJump,
    cardList?.isJump2,
    type,
    isStudioCalendar,
  ]);

  // 有数据的情况下，过期且鼠标不hover/过期且不可跳转时透明度0.5，其它正常
  const isTranstparent = useMemo<boolean>(
    () => !isNoData && ((isOverdue && !isHoverCard) || (isOverdue && !isPageSkipable)),
    [isOverdue, isHoverCard, isPageSkipable, isNoData],
  );

  const platform = useMemo<string | undefined>(() => {
    if (Array.isArray(cardList?.platform)) {
      const newPlatform = cardList?.platform?.map((item: any) => PLATFORM_NAMES[item]) || [];
      return newPlatform?.length > 0 ? newPlatform.join(',') : '';
    }
    if (cardList?.platform?.toLowerCase() === 'all platforms') return 'Mobile,PC,Console';
    return cardList?.platform?.replaceAll('m', 'M')?.replaceAll('p', 'P')?.replaceAll('c', 'C');

    // if (cardList?.platform?.toLowerCase() === 'all platforms') return 'Mobile,PC,Console';

    // return cardList?.platform?.replaceAll('/', ',')?.replaceAll('m', 'M')?.replaceAll('p', 'P')?.replaceAll('c', 'C');
  }, [cardList?.platform]);

  const milestoneHistories = useMemo<IMilestoneHistory[]>(() => {
    if (!isDashboardCalendar) return [];
    const result = cardList?.milestone_history;
    return Array.isArray(result) ? result : [];
  }, [isDashboardCalendar, cardList]);

  const isShowTimeChanges = useMemo<boolean>(
    () => isDashboardCalendar && milestoneHistories?.length > 0,
    [isDashboardCalendar, milestoneHistories],
  );

  const colorType = useMemo(() => {
    if (!isEmpty(cardList) && cardList?.category) {
      if (cardList?.category === 'gr') {
        return 1;
      }
      if (cardList?.category === 'check_in') {
        return 0;
      }
      return 3;
    }
    return type;
  }, [type, cardList]);

  // 卡片点击跳转
  const handleClick = useMemoizedFn(
    (isJump: boolean, e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!queryJumpable(e)) return;
      if (!isPageSkipable) return;
      if (typeof onCardClick === 'function') return onCardClick?.(cardList, isJump);
      /**
       * 以下跳转是Dashboard页面卡片的跳转
       */
      // 跳转产品详情页
      const filter = encodeURIComponent(
        JSON.stringify({
          projectId: cardList?.projectId || '',
          project: cardList?.projectName || '',
          menuFrom: MENU_ID_MAP.pipeline_dashboard, // 用于标识左侧菜单栏当前选中高亮
          tab: isDashboardCalendar ? 'milestone' : 'gameDetail',
        }),
      );
      const url = `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
      if (isJump) {
        history.push(url);
      }

      return url;
    },
  );

  return (
    <div style={style} className={classnames('dbg-schedule-calendar-box', className)}>
      <LinkJump url={handleClick(false) || ''}>
        <div
          className={classnames('dbg-schedule-card font14', { 'is-no-skip': !isPageSkipable })}
          onClick={(e) => handleClick(true, e)}
          onMouseEnter={() => setIsHoverCard(true)}
          onMouseLeave={() => setIsHoverCard(false)}
        >
          {isNoData && <span className="schedule-calendar-no-data-card">No Data</span>}
          {isNoData || (
            <>
              <div
                style={{ backgroundColor: isTranstparent ? BG_MAP?.[2] : BG_MAP?.[colorType] }}
                className="card-top-bar"
              ></div>
              {/* @ts-ignore*/}
              <Bubble
                className={classnames(
                  'dbg-bubble-min-width dbg-bubble-max-width dbg-time-change-bubble',
                  className,
                )}
                dark
                placement="right"
                positionFixed={false}
                popupContainer={fullScreenContent?.current as any}
                content={
                  !isShowTimeChanges ? undefined : (
                    <ChangeLogs
                      className="dbg-time-change-logs"
                      data={getMilestoneHistories(getSortedMilestoneHistories(milestoneHistories))}
                    />
                  )
                }
              >
                <div className="schedule-calendar-card-top">
                  <div className="schedule-calendar-card-top-left">
                    {isShowTimeChanges && (
                      <div className="dbg-time-change-icon-wrapper">
                        <SvgIcon className="dbg-time-change-icon" type="updating" />
                      </div>
                    )}
                    <span className="project-date">{date || '-'}</span>
                    {isTBC && <span className="tbc-tag">TBC</span>}
                  </div>
                  <div className="schedule-calendar-card-top-right">
                    <label className="project-stage">
                      {getMilestoneCorrectName(cardList?.milestone) || '-'}
                    </label>
                  </div>
                </div>
              </Bubble>
              <Bubble
                className="dbg-bubble-min-width dbg-bubble-max-width globalpedia-modal-font14 dbg-bubble-arrow-border-top-color"
                dark
                placement="right"
                positionFixed={false}
                placementOffset={[0, 30]}
                popupContainer={fullScreenContent?.current as any}
                content={
                  isNoData ? undefined : (
                    <div
                      className="dbg-calendar-card-bubble-content"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <p className="dbg-word-break-title">{cardList?.projectName || '-'}</p>
                      <p className="dbg-word-break-sub-title">
                        <span>{getMilestoneCorrectName(cardList?.milestone) || '-'}</span>
                        <span className="dbg-word-break-sub-title-date">
                          <span>{date || '-'}</span>
                          {isTBC && (
                            // <img className="dbg-word-break-title-tbc-new" src={ICON_TBC} alt="" />
                            <span className="dbg-calendar-milestone-bubble-tbc">TBC</span>
                          )}
                        </span>
                      </p>
                      <div className="dbg-word-break-divider"></div>
                      <div className="dbg-word-break-context">
                        <p className="dbg-word-break-text">Studio: {cardList?.studio || '-'}</p>
                        <p className="dbg-word-break-text">Genre: {cardList?.genre || '-'}</p>
                        <p className="dbg-word-break-text">Platform: {platform || '-'}</p>
                        <p className="dbg-word-break-text">BO: {cardList?.bo || '-'}</p>
                      </div>
                    </div>
                  )
                }
              >
                <div className="schedule-calendar-card-body">
                  <GeneralImageNew
                    style={{ position: 'relative' }}
                    width={60}
                    height={60}
                    src={cardList?.cover || ''}
                  />
                  <div className="project-info">
                    <span className="project-name">
                      {(isStudioCalendar
                        ? type === 0
                          ? cardList?.studio
                          : cardList?.projectName
                        : cardList?.projectName) || '-'}
                    </span>
                    <div className="project-studio-flex">
                      <span className="project-studio">
                        {(isStudioCalendar
                          ? type === 0
                            ? cardList?.bo
                            : cardList?.studio
                          : cardList?.studio) || '-'}
                      </span>
                    </div>
                    <div className="schedule-calendar-card-body__helper-info-container">
                      {isStudioCalendar && type === 1 && (
                        <>
                          {/* <i className="calendar-card-footer-right-info-clapboard"></i>*/}
                          <PlatformIcon platform={cardList?.platform} />
                        </>
                      )}
                      {isDashboardCalendar && (
                        <>
                          {/* <i className="calendar-card-footer-right-info-clapboard"></i>*/}
                          <PlatformIcon platform={cardList?.platform} />
                        </>
                      )}
                      {isDashboardCalendar &&
                        PIPELINE_OVERVIEW_SCHEDULE_GATEREVIEW_DECISIONS_CODES.includes(
                          cardList?.gate_review_status,
                        ) && <GRStatusTag status={cardList?.gate_review_status} />}
                    </div>
                  </div>
                </div>
              </Bubble>
            </>
          )}
        </div>
      </LinkJump>
    </div>
  );
};

export default React.memo(Card);
